import { FormEvent, ReactNode, useEffect, useRef, useState } from "react";
import { Col, Form, Row } from "react-bootstrap";
import {
  generatedV2,
  useV2ListCrmOpportunitiesForAccountQuery,
  useV2ListSalesExecutivesForAccountQuery,
} from "@generated";
import { TextField, DateField } from "@components/FormsV2";
import TagsField from "@components/FormsV2/TagsField";
import SearchSelect, {
  SearchSelectOption,
} from "@components/FormsV2/SearchSelect";
import AutoCompleteText from "@components/Forms/AutoCompleteText/AutoCompleteText";
import AddressForm from "@components/Forms/AddressForm";
import ClientRecordPicker, {
  PickedRecord,
} from "@components/ClientRecordPicker/ClientRecordPicker";
import {
  siteLinkPayload,
  toCountryId,
} from "@components/ClientRecordPicker/siteLink";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import useInfiniteSelectOptions from "@components/FormsV2/Select2/useInfiniteSelectOptions";
import useFlags from "@common/hooks/useFlags";
import usePrivilegeCheck from "@common/hooks/usePrivilegeCheck";
import ProjectVariables from "../../ProjectVariables/ProjectVariables";
import {
  crmLocationValue,
  validateLocation,
} from "../projectUtils/projectHelperFunctions";
import {
  FormVariable,
  useProjectFormOptions,
} from "./api/useProjectFormOptions";
import type {
  ApiRecord,
  NewProjectValues,
  ProjectFormInitialValues,
} from "./types";
import "./ProjectForm.css";
import {
  EMPTY_LOCATION,
  LocationDraft,
  fillVariables,
  isAddressChanged,
  isLocationEntered,
  toVariableValues,
  variableLengthError,
  withSelectDefaults,
} from "./formHelpers";

export interface ProjectFormProps {
  id: string;
  // Shows a spinner in place of the form, which stays mounted so a failed save
  // comes back with its values.
  isSubmitting?: boolean;
  onSubmit: (values: NewProjectValues) => void;
  // Called on every change to a field, e.g. to warn about unsaved changes.
  onEdit?: () => void;
  // Starts on a saved project instead of the account's defaults.
  initialValues?: ProjectFormInitialValues;
  // Shows every field disabled.
  readOnly?: boolean;
  // Wrap or replace the CRM select / the location fields, e.g. a linked
  // opportunity with Remove, or the links for a project with several locations.
  renderCrm?: (field: ReactNode, label: string) => ReactNode;
  renderLocation?: (fields: ReactNode) => ReactNode;
}

const findOption = (options: SearchSelectOption[], value: string) =>
  options.find((o) => o.value === value) ?? null;

// Keeps a saved record selectable when its list no longer has it (inactive).
const withSaved = (
  options: SearchSelectOption[],
  saved?: SearchSelectOption | null
) =>
  saved && !options.some((o) => o.value === saved.value)
    ? [...options, saved]
    : options;

const identity = (node: ReactNode) => node;

/**
 * The project fields, shared by the create and edit pages: it collects and
 * validates the project, and hands the caller `NewProjectValues`.
 */
const ProjectForm = ({
  id,
  isSubmitting = false,
  onSubmit,
  onEdit = () => null,
  initialValues,
  readOnly = false,
  renderCrm = identity,
  renderLocation = identity,
}: ProjectFormProps): JSX.Element => {
  const options = useProjectFormOptions();
  const presalesOptions = withSaved(
    options.presalesOptions,
    initialValues?.presalesEngineer
  );
  const rateTableOptions = withSaved(
    options.rateTableOptions,
    initialValues?.rateTable
  );
  const { fieldLabels: labels } = options;
  const {
    configCrmOpportunityRequired,
    configSalesExecutiveRequired,
    configPresalesEngineerRequired,
    configBusinessUnitRequired,
    configMsaDateHidden,
    configServiceLocationRequired,
  } = useFlags();
  const { accessLevel } = usePrivilegeCheck();
  const canCreateSalesExec = ["manage", "create"].includes(
    accessLevel("settings.sales_executives") ?? ""
  );

  const [projectName, setProjectName] = useState("");
  const [clientName, setClientName] = useState("");
  const [clientId, setClientId] = useState<string | null>(null);
  const [clientRecords, setClientRecords] = useState<ApiRecord[]>([]);
  const [crm, setCrm] = useState<SearchSelectOption | null>(null);
  const [salesExec, setSalesExec] = useState<SearchSelectOption | null>(null);
  const [presalesEngineerId, setPresalesEngineerId] = useState("");
  const [paymentTermId, setPaymentTermId] = useState("");
  const [rateTableId, setRateTableId] = useState("");
  const [businessUnitId, setBusinessUnitId] = useState("");
  const [msaDate, setMsaDate] = useState<string | null>(null);
  const [tags, setTags] = useState<string[]>([]);
  const [projectVariables, setProjectVariables] = useState<FormVariable[]>([]);
  const [location, setLocation] = useState<LocationDraft>(EMPTY_LOCATION);
  const [locationVariables, setLocationVariables] = useState<FormVariable[]>(
    []
  );
  // What the location variables started as; a change from it enters a location.
  const [startingLocationVariables, setStartingLocationVariables] = useState<
    FormVariable[]
  >([]);
  const [toast, setToast] = useState<string | null>(null);

  // An opportunity unlinked on the edit page clears here; other edits stay.
  const savedCrm = initialValues?.crmOpportunity;
  useEffect(() => {
    if (!savedCrm) setCrm(null);
  }, [savedCrm]);

  // Starts on the saved project, or the account's defaults, once the option
  // lists arrive.
  const initialized = useRef(false);
  useEffect(() => {
    if (options.isLoading || initialized.current) return;
    initialized.current = true;
    const startingLocation = withSelectDefaults(
      fillVariables(
        options.locationVariables,
        initialValues?.location?.projectVariables ?? []
      )
    );
    setLocationVariables(startingLocation);
    setStartingLocationVariables(startingLocation);
    if (!initialValues) {
      setPaymentTermId(options.defaults.paymentTermId ?? "");
      setRateTableId(options.defaults.rateTableId ?? "");
      setBusinessUnitId(options.defaults.businessUnitId ?? "");
      setProjectVariables(withSelectDefaults(options.projectVariables));
      return;
    }
    setProjectName(initialValues.projectName);
    setClientName(initialValues.client.name);
    setClientId(initialValues.client.id);
    setCrm(initialValues.crmOpportunity);
    setSalesExec(initialValues.salesExecutive);
    setPresalesEngineerId(initialValues.presalesEngineer?.value ?? "");
    setPaymentTermId(initialValues.paymentTermId ?? "");
    setRateTableId(initialValues.rateTable?.value ?? "");
    setBusinessUnitId(initialValues.businessUnitId ?? "");
    setMsaDate(initialValues.msaDate);
    setTags(initialValues.tags);
    setProjectVariables(
      withSelectDefaults(
        fillVariables(options.projectVariables, initialValues.projectVariables)
      )
    );
    if (initialValues.location) {
      // The draft is the address alone; the variables are set above.
      const {
        projectVariables: _variables,
        link: _link,
        ...address
      } = initialValues.location;
      setLocation({ ...EMPTY_LOCATION, ...address });
    }
  }, [options.isLoading]);

  const crmRecords = useRef(new Map<string, ApiRecord>());
  const crmSelect = useInfiniteSelectOptions({
    useQuery: useV2ListCrmOpportunitiesForAccountQuery,
    searchFilter: "display-name",
    toOption: (record: ApiRecord) => {
      crmRecords.current.set(String(record.id), record);
      return {
        value: String(record.id),
        label: record.attributes?.["display-name"] ?? "",
      };
    },
  });
  const salesExecSelect = useInfiniteSelectOptions({
    useQuery: useV2ListSalesExecutivesForAccountQuery,
    toOption: (record: ApiRecord) => ({
      value: String(record.id),
      label: record.attributes?.name ?? "",
    }),
  });

  // toOption above only makes string values.
  const crmOptions = crmSelect.options as SearchSelectOption[];
  const salesExecOptions = salesExecSelect.options as SearchSelectOption[];

  const [searchClients] =
    generatedV2.endpoints.v2ListClientsForAccount.useLazyQuery();
  const latestClientSearch = useRef("");

  const applyClient = (record: ApiRecord) => {
    setClientId(String(record.id));
    setMsaDate(record.attributes?.["msa-date"] ?? null);
    const rateTable = record.relationships?.["rate-table"]?.data?.id;
    if (
      rateTable &&
      rateTableOptions.some((o) => o.value === String(rateTable))
    ) {
      setRateTableId(String(rateTable));
    }
  };

  // Suggestions follow what's typed; an exact name match is that client.
  const loadClients = (name: string, onMatch?: (record: ApiRecord) => void) => {
    latestClientSearch.current = name;
    searchClients({
      filter: { ...(name && { name }), active: true },
      page: { size: 100 },
      include: ["rate-table"],
    })
      .unwrap()
      .then((res) => {
        if (latestClientSearch.current !== name) return;
        const records = (res.data ?? []) as ApiRecord[];
        setClientRecords(records);
        const match = records.find((r) => r.attributes?.name === name);
        if (match && onMatch) onMatch(match);
      })
      .catch(() => setClientRecords([]));
  };

  const changeClient = (name: string) => {
    setClientName(name);
    setClientId(null);
    loadClients(name, applyClient);
  };

  // Loads suggestions only, so a saved client keeps its MSA date and rate table.
  useEffect(() => {
    loadClients("");
  }, []);

  // A pick belongs to the client / opportunity it was searched under.
  useEffect(() => {
    setLocation((l) => (l.picked ? { ...l, picked: null } : l));
  }, [clientId, crm?.value]);

  const applyPick = (picked: PickedRecord) => {
    onEdit();
    setLocation({
      ...EMPTY_LOCATION,
      name: picked.fields.name ?? "",
      street: picked.fields.street ?? "",
      street2: picked.fields.street2 ?? "",
      city: picked.fields.city ?? "",
      state: picked.fields.state ?? "",
      postalCode: picked.fields.postalCode ?? "",
      countryId: toCountryId(picked.fields.country) ?? "",
      picked,
    });
  };

  const findSalesExec = (name: string): SearchSelectOption =>
    salesExecOptions.find((o) => o.label === name) ?? {
      value: "",
      label: name,
    };

  const changeCrm = (option: SearchSelectOption | null) => {
    onEdit();
    const record = option?.value
      ? crmRecords.current.get(option.value)
      : undefined;
    if (!option?.value || !record) {
      setCrm(null);
      return;
    }
    setCrm(option);
    const a = record.attributes ?? {};
    const custom: { key: string; value: string }[] =
      a["custom-attributes"] ?? [];

    setProjectName(a.name ?? "");
    changeClient(a["account-name"] ?? "");
    setLocation({
      ...EMPTY_LOCATION,
      name: crmLocationValue(a["location-name"] ?? a["account-name"]),
      street: crmLocationValue(a.street),
      street2: crmLocationValue(a.street2),
      city: crmLocationValue(a.city),
      state: crmLocationValue(a.state),
      postalCode: crmLocationValue(a["postal-code"]),
      countryId: toCountryId(a.country) ?? "",
    });
    const customValues = custom.map(({ key, value }) => ({ name: key, value }));
    setProjectVariables((vars) => fillVariables(vars, customValues));
    setLocationVariables((vars) => fillVariables(vars, customValues));

    const presalesName = custom.find(
      (c) => c.key === "sow_presales_engineer_id"
    )?.value;
    const presales = presalesOptions.find((o) => o.label === presalesName);
    if (presales) setPresalesEngineerId(presales.value);

    const owner =
      a["owner-name"] ??
      custom.find((c) => c.key === "client_manager_name")?.value;
    if (owner) setSalesExec(findSalesExec(owner));
  };

  // A location variable changed from its starting value also counts, and
  // changing it back releases the location again.
  const locationVariableChanged = locationVariables.some(
    (v) =>
      v.value !==
      (startingLocationVariables.find((o) => o.name === v.name)?.value ?? "")
  );
  const locationEntered =
    isLocationEntered(location) || locationVariableChanged;
  // A saved location is only checked and saved again once it changes, so one
  // saved incomplete doesn't block other edits.
  const savedLocation = initialValues?.location;
  const submitsLocation =
    locationEntered &&
    (!savedLocation ||
      locationVariableChanged ||
      isAddressChanged(location, savedLocation));
  const setLocationField =
    (key: keyof LocationDraft) => (e: { target: { value: string } }) =>
      setLocation((l) => ({ ...l, [key]: e.target.value }));

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setToast(null);

    const lengthError = variableLengthError([
      ...projectVariables,
      ...(submitsLocation ? locationVariables : []),
    ]);
    if (lengthError) {
      setToast(
        lengthError.kind === "min"
          ? `Required text is too short (minimum is ${lengthError.limit} characters)`
          : `Required text is too long (maximum is ${lengthError.limit} characters)`
      );
      return;
    }
    if (submitsLocation) {
      let locationError = "";
      const valid = validateLocation(
        true,
        location.name,
        location.street,
        location.city,
        location.state,
        location.postalCode,
        location.remote,
        (message: string) => (locationError = message)
      );
      if (!valid) {
        setToast(locationError);
        return;
      }
    }

    const { picked, ...address } = location;
    onSubmit({
      projectName: projectName.trim(),
      client: { id: clientId, name: clientName.trim() },
      salesExecutive: salesExec
        ? { id: salesExec.value || null, name: salesExec.label }
        : null,
      crmOpportunityId: crm?.value ?? null,
      presalesEngineerId: presalesEngineerId || null,
      paymentTermId: paymentTermId || null,
      rateTableId: rateTableId || null,
      businessUnitId: businessUnitId || null,
      msaDate: msaDate || null,
      tags,
      projectVariables: toVariableValues(projectVariables),
      location: submitsLocation
        ? {
            ...address,
            projectVariables: toVariableValues(locationVariables),
            link: picked ? siteLinkPayload(picked) : null,
          }
        : null,
    });
  };

  if (options.isLoading) return <ScopeStackSpinner />;

  const required = (label: string, isRequired: boolean | undefined) =>
    isRequired ? `${label} *` : label;
  const crmLabel = required(
    labels.crm_opportunity,
    configCrmOpportunityRequired
  );
  const salesExecLabel = required(
    labels.sales_executive,
    configSalesExecutiveRequired
  );
  // react-select picks fire no native change event for the form to report.
  const edited =
    <T,>(change: (value: T) => void) =>
    (value: T) => {
      onEdit();
      change(value);
    };

  return (
    <>
      {isSubmitting && <ScopeStackSpinner />}
      <Form
        id={id}
        data-testid={id}
        className="createProjectForm"
        style={isSubmitting ? { display: "none" } : undefined}
        onSubmit={handleSubmit}
        onChange={() => onEdit()}
      >
        {toast && (
          <ToastAlert
            variant="warning"
            text={toast}
            onClose={() => setToast(null)}
          />
        )}
        {/* Disables every native field at once when read-only. */}
        <fieldset disabled={readOnly}>
          {options.isCrm &&
            renderCrm(
              <SearchSelect
                id="crmOpportunity"
                label={crmLabel}
                value={crm}
                options={crmOptions}
                onChange={changeCrm}
                onInputChange={crmSelect.onInputChange}
                onMenuScrollToBottom={crmSelect.onMenuScrollToBottom}
                isLoading={crmSelect.isLoading}
                required={!!configCrmOpportunityRequired}
                isClearable={!configCrmOpportunityRequired}
                isDisabled={readOnly}
                placeholder={`Select ${labels.crm_opportunity}`}
              />,
              crmLabel
            )}
          <Row>
            <Col xs={6}>
              <TextField
                id="projectName"
                label="Project Name *"
                required
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
              />
              <Row>
                <Col sm={6}>
                  <SearchSelect
                    id="salesExecutive"
                    label={salesExecLabel}
                    value={salesExec}
                    options={salesExecOptions}
                    onChange={edited(setSalesExec)}
                    onInputChange={salesExecSelect.onInputChange}
                    onMenuScrollToBottom={salesExecSelect.onMenuScrollToBottom}
                    isLoading={salesExecSelect.isLoading}
                    isCreatable={canCreateSalesExec}
                    required={!!configSalesExecutiveRequired}
                    isClearable={!configSalesExecutiveRequired}
                    isDisabled={readOnly}
                  />
                </Col>
                <Col sm={6}>
                  <SearchSelect
                    id="presalesEngineer"
                    label={required(
                      labels.presales_engineer,
                      configPresalesEngineerRequired
                    )}
                    value={findOption(presalesOptions, presalesEngineerId)}
                    options={presalesOptions}
                    onChange={edited((option) =>
                      setPresalesEngineerId(option?.value ?? "")
                    )}
                    required={!!configPresalesEngineerRequired}
                    isClearable={!configPresalesEngineerRequired}
                    isDisabled={readOnly}
                  />
                </Col>
                <Col sm={6}>
                  <SearchSelect
                    id="paymentTerm"
                    label="Payment Term *"
                    value={findOption(
                      options.paymentTermOptions,
                      paymentTermId
                    )}
                    options={options.paymentTermOptions}
                    onChange={edited((option) =>
                      setPaymentTermId(option?.value ?? "")
                    )}
                    required
                    isDisabled={readOnly}
                  />
                </Col>
                {rateTableOptions.length > 1 && (
                  <Col sm={6}>
                    <SearchSelect
                      id="rateTable"
                      label="Rate Table *"
                      value={findOption(rateTableOptions, rateTableId)}
                      options={rateTableOptions}
                      onChange={edited((option) =>
                        setRateTableId(option?.value ?? "")
                      )}
                      required
                      isDisabled={readOnly}
                    />
                  </Col>
                )}
                {options.businessUnitOptions.length > 0 && (
                  <Col sm={6}>
                    <SearchSelect
                      id="businessUnit"
                      label={required(
                        labels.business_unit,
                        configBusinessUnitRequired
                      )}
                      value={findOption(
                        options.businessUnitOptions,
                        businessUnitId
                      )}
                      options={options.businessUnitOptions}
                      onChange={edited((option) =>
                        setBusinessUnitId(option?.value ?? "")
                      )}
                      required={!!configBusinessUnitRequired}
                      isClearable={!configBusinessUnitRequired}
                      isDisabled={readOnly}
                    />
                  </Col>
                )}
                {!configMsaDateHidden && (
                  <Col sm={6}>
                    <DateField
                      id="msaDate"
                      label="MSA Date"
                      value={msaDate ?? ""}
                      onChange={(e) => setMsaDate(e.target.value || null)}
                    />
                  </Col>
                )}
                <ProjectVariables
                  account_slug={null}
                  arr={projectVariables}
                  project_id={null}
                  setState={setProjectVariables}
                  setVariableChanged={null}
                />
              </Row>
            </Col>
            <Col xs={6}>
              {crm || readOnly ? (
                <TextField
                  id="clientName"
                  label={`${labels.client} *`}
                  value={clientName}
                  readOnly
                  onChange={() => null}
                />
              ) : (
                <AutoCompleteText
                  placeholder=""
                  id="clientName"
                  label={`${labels.client} *`}
                  required
                  value={clientName}
                  suggestionList={clientRecords.map(
                    (r) => r.attributes?.name ?? ""
                  )}
                  monitorValue={changeClient}
                  setTextChanged={() => null}
                  crmId={null}
                />
              )}
              {renderLocation(
                <>
                  {/* A saved location keeps the site it has. */}
                  {!readOnly && !initialValues?.location && (
                    <ClientRecordPicker
                      kind="site"
                      clientId={clientId}
                      crmOpportunityId={crm?.value ?? null}
                      onPick={applyPick}
                    />
                  )}
                  <AddressForm
                    label={required(
                      labels.location,
                      configServiceLocationRequired
                    )}
                    required={!!configServiceLocationRequired}
                    placeholder="Customer Site"
                    siteName={location.name}
                    onSiteChange={(e) =>
                      setLocation((l) => ({
                        ...l,
                        name: e.target.value,
                        picked: e.target.value ? l.picked : null,
                      }))
                    }
                    addressText={location.street}
                    onAddressChange={setLocationField("street")}
                    street2Text={location.street2}
                    onStreet2Change={setLocationField("street2")}
                    cityText={location.city}
                    onCityChange={setLocationField("city")}
                    stateText={location.state}
                    onStateChange={setLocationField("state")}
                    zipText={location.postalCode}
                    onZipChange={setLocationField("postalCode")}
                    remoteLocation={location.remote}
                    onRemoteChanged={(e) =>
                      setLocation((l) => ({ ...l, remote: e.target.checked }))
                    }
                    country={location.countryId}
                    onCountryChange={edited((country: { id?: string } | null) =>
                      setLocation((l) => ({
                        ...l,
                        countryId: country?.id ?? "",
                      }))
                    )}
                  />
                  <Row>
                    <ProjectVariables
                      account_slug={null}
                      overridePadding
                      arr={locationVariables.map((v) => ({
                        ...v,
                        required: v.required && submitsLocation,
                      }))}
                      project_id={null}
                      // Keeps each variable's own required flag; only values come back.
                      setState={(next: FormVariable[]) =>
                        setLocationVariables((vars) =>
                          vars.map((v) => ({
                            ...v,
                            value:
                              next.find((n) => n.name === v.name)?.value ??
                              v.value,
                          }))
                        )
                      }
                      setVariableChanged={null}
                    />
                  </Row>
                </>
              )}
              <TagsField
                id="tags"
                value={tags}
                onChange={edited(setTags)}
                isDisabled={readOnly}
              />
            </Col>
          </Row>
        </fieldset>
      </Form>
    </>
  );
};

export default ProjectForm;
